import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Menu,
  Select,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import {
  IconChevronRight,
  IconDots,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { repositoryPos } from "../../../_base/_const/_constVar";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import { getMasterOrgSelect } from "../../../service/getSelectApi";
import ReportScopeDrawer, { ReportScopeTarget } from "./ReportScopeDrawer";

type ReportItem = {
  code: string;
  name: string;
  listBranchId: string;
  listDepartmentId: string;
};

type AuthReadingByEmployee = {
  empId: number;
  empCode: string;
  empName: string;
  reportCount: number;
  /** Mọi báo cáo cùng một danh sách chi nhánh thì mới hiện được ở dòng chính */
  isSameBranch: boolean;
  isSameDepartment: boolean;
  listBranchId: string;
  listDepartmentId: string;
  reports: ReportItem[];
};

/**
 * Xem phân quyền báo cáo theo chiều nhân viên: mỗi người một dòng.
 *
 * Cách xem cũ đi từ báo cáo, phải mở từng báo cáo mới biết ai được cấp.
 * Ở đây đi ngược lại, nhìn phát biết một người đang xem được những báo cáo nào
 * và trong phạm vi chi nhánh, phòng ban nào.
 */
const ReportByEmployee = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [data, setData] = useState<AuthReadingByEmployee[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [keySearch, setKeySearch] = useDebouncedState<string>("", 400);
  const [dataBranch, setDataBranch] = useState<any[]>([]);
  const [dataDepartment, setDataDepartment] = useState<any[]>([]);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 30,
  });
  // Có giá trị là ngăn kéo đang mở, null là đóng
  const [scopeTarget, setScopeTarget] = useState<ReportScopeTarget | null>(
    null,
  );

  /**
   * Một thẻ báo cáo bấm được: bấm vào mở ngăn kéo sửa phạm vi của đúng báo cáo đó.
   *
   * Phạm vi cấp riêng cho từng cặp nhân viên - báo cáo nên không hiện gộp ở ngoài được,
   * phải mở từng cái ra xem mới đúng.
   */
  const renderReportBadge = (row: AuthReadingByEmployee, item: ReportItem) => (
    <Tooltip key={item.code} label={`${item.code} - Bấm để sửa phạm vi`}>
      <Badge
        variant="light"
        color="teal"
        size="sm"
        tt="none"
        fw={500}
        style={{ cursor: "pointer" }}
        rightSection={<IconChevronRight size={12} />}
        onClick={() =>
          setScopeTarget({
            empId: row.empId,
            empCode: row.empCode,
            empName: row.empName,
            code: item.code,
            name: item.name,
            listBranchId: item.listBranchId,
            listDepartmentId: item.listDepartmentId,
          })
        }
      >
        {item.name && item.name.length > 0 ? item.name : item.code}
      </Badge>
    </Tooltip>
  );

  const columns = React.useMemo<MRT_ColumnDef<AuthReadingByEmployee>[]>(
    () => [
      {
        header: "#",
        accessorKey: "stt",
        size: 60,
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <>{pagination.pageIndex * pagination.pageSize + row.index + 1}</>
        ),
      },
      {
        accessorKey: "empName",
        header: "Nhân viên",
        size: 220,
        Cell: ({ row }) => (
          <>
            {row.original.empCode} - {row.original.empName}
          </>
        ),
      },
      {
        accessorKey: "reports",
        header: "Danh sách báo cáo",
        size: 620,
        enableSorting: false,
        Cell: ({ row }) => {
          const reports = row.original.reports ?? [];

          return (
            <Flex gap="xs" align="center" wrap="nowrap">
              {reports
                .slice(0, 3)
                .map((item) => renderReportBadge(row.original, item))}

              {reports.length > 3 && (
                <Menu width={500} closeOnItemClick={false}>
                  <Menu.Target>
                    <Tooltip label="Xem thêm">
                      <ActionIcon variant="light" color="teal" radius="lg">
                        <IconDots size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Flex gap="xs" wrap="wrap" justify="center">
                      {reports
                        .slice(3)
                        .map((item) => renderReportBadge(row.original, item))}
                    </Flex>
                  </Menu.Dropdown>
                </Menu>
              )}

              <Tooltip label="Cấp thêm báo cáo">
                <ActionIcon
                  variant="light"
                  color="blue"
                  radius="lg"
                  onClick={() =>
                    setScopeTarget({
                      empId: row.original.empId,
                      empCode: row.original.empCode,
                      empName: row.original.empName,
                      grantedCodes: reports.map((item) => item.code),
                    })
                  }
                >
                  <IconPlus size={18} />
                </ActionIcon>
              </Tooltip>
            </Flex>
          );
        },
      },
    ],
    // dataBranch và dataDepartment phải nằm ở đây, không thì thẻ hiện mã thay vì tên
    // vì hàm vẽ giữ lại danh sách rỗng của lần dựng đầu
    [dataBranch, dataDepartment, pagination.pageIndex, pagination.pageSize],
  );

  useEffect(() => {
    fetchBranch();
    fetchDepartment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => {
      // Đo ngay lúc đổi kích thước vì thanh tìm kiếm xuống dòng khi màn hình hẹp
      const headerHeight = headerRef.current?.offsetHeight || 0;
      // 280 gồm đường dẫn điều hướng, thanh chọn tab và phần phân trang
      setHeight(window.innerHeight - (280 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize]);

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    try {
      let url = `/api/v1/AuthReportReading/get-list-auth-reading-by-employee?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      if (keySearch && keySearch.length > 0)
        url += `&KeySearch=${encodeURIComponent(keySearch)}`;

      // "-1" là mục Tất cả, không phải một chi nhánh hay phòng ban thật
      if (branchId && branchId !== "-1") url += `&BranchId=${branchId}`;

      if (departmentId && departmentId !== "-1")
        url += `&DepartmentId=${departmentId}`;

      const res =
        await repositoryPos.get<MessageResponse<AuthReadingByEmployee[]>>(url);

      if (res && res.success) {
        setData(res.data ?? []);
        setRowCount(res.totalCount ?? 0);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error(error);
      setData([]);
      setRowCount(0);
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  // Dùng lại đúng nguồn và đúng cách ghép nhãn của màn phân quyền cũ,
  // để tên chi nhánh và phòng ban ở hai cách xem hiện giống hệt nhau.
  const fetchBranch = async () => {
    try {
      const res = await getMasterOrgSelect();

      if (res) {
        const list = res
          ?.filter((item: any) => item.text && item.value && item.att1)
          ?.map((item: any) => ({
            label: `${item.att1} - ${item.text}`,
            value: item.value,
          }));
        setDataBranch([{ label: "Tất cả", value: "-1" }, ...(list ?? [])]);
      }
    } catch (error) {
      console.error("Không lấy được danh sách chi nhánh", error);
    }
  };

  const fetchDepartment = async () => {
    try {
      const res = await repositoryPos.get<MessageResponse<any>>(
        "/api/v1/TblDepartment/get-all",
      );

      if (res && res.success) {
        // Nhiều chi nhánh có phòng ban trùng tên, ghép thêm tên chi nhánh để phân biệt
        const list = res.data
          ?.filter(
            (item: any) => item.departmentId && item.name && item.deparmentCode,
          )
          ?.map((item: any) => ({
            label: `[${item.attribute1}] - ${item.deparmentCode} - ${item.name}`,
            value: item.departmentId?.toString(),
          }));
        setDataDepartment([{ label: "Tất cả", value: "-1" }, ...(list ?? [])]);
      }
    } catch (error) {
      console.error("Không lấy được danh sách phòng ban", error);
    }
  };

  const table = useMantineReactTable({
    columns,
    data,
    localization: _localization,
    enableTopToolbar: false,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableRowSelection: false,
    manualPagination: true,
    rowCount,
    onPaginationChange: setPagination,
    state: {
      isLoading,
      pagination,
      showProgressBars: isRefetching,
    },
    // Ép cứng cùng một chiều cao để bảng luôn chạm đáy màn hình,
    // dữ liệu ít hay nhiều thì phần phân trang vẫn nằm nguyên một chỗ
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    // Mỗi dòng chứa khá nhiều thẻ, để chữ nhỏ lại cho đỡ chật
    mantineTableBodyCellProps: {
      style: { fontSize: "12px", padding: "5px 12px" },
    },
    mantineTableHeadCellProps: {
      style: { fontSize: "12.5px" },
    },
    initialState: { density: "xs" },
  });

  return (
    <Box>
      <Grid ref={headerRef} mt={5} mb={10} align="center" gutter="xs">
        <Grid.Col span={{ base: 12, xs: 6, md: 4, lg: 3 }}>
          <TextInput
            placeholder="Tìm nhân viên hoặc báo cáo ..."
            defaultValue={keySearch}
            onChange={(e) => setKeySearch(e.currentTarget.value ?? "")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, xs: 6, md: 4, lg: 3 }}>
          <Select
            placeholder="Chi nhánh"
            data={dataBranch}
            value={branchId}
            onChange={setBranchId}
            searchable
            clearable
            nothingFoundMessage="Không tìm thấy chi nhánh !"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, xs: 6, md: 4, lg: 3 }}>
          <Select
            placeholder="Phòng ban"
            data={dataDepartment}
            value={departmentId}
            onChange={setDepartmentId}
            searchable
            clearable
            nothingFoundMessage="Không tìm thấy phòng ban !"
          />
        </Grid.Col>
        <Grid.Col span="content">
          <Button
            leftSection={<IconSearch size={14} />}
            color="blue"
            variant="outline"
            onClick={async () => {
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              await fetchData();
            }}
          >
            Tìm kiếm
          </Button>
        </Grid.Col>
      </Grid>

      <MantineReactTable table={table} />

      <ReportScopeDrawer
        target={scopeTarget}
        dataBranch={dataBranch}
        dataDepartment={dataDepartment}
        onClose={() => setScopeTarget(null)}
        onSaved={fetchData}
      />
    </Box>
  );
};

export default ReportByEmployee;
